import { defineMessages } from 'react-intl';

/**
 * The copy of the Auto campaign tab of a release (design rollout-plan f1-f6, spec 2026-10-12). The
 * ids keep the first name of the feature (`reelsPlan`); the user reads "campaign". The word "plan"
 * stays only for a subscription plan. The step cards, the channel pills and the step sheet keep the copy of the rollout (`rolloutMessages`,
 * `sheetMessages`).
 */
export const reelsPlanMessages = defineMessages({
  // ---- Setup (f1, f5)
  title: { id: 'app.sml.promote.reelsPlan.title', defaultMessage: 'Auto campaign' },
  subtitle: {
    id: 'app.sml.promote.reelsPlan.subtitle',
    defaultMessage: 'A different AI reel of this song on each day',
  },
  subtitleNext: { id: 'app.sml.promote.reelsPlan.subtitleNext', defaultMessage: 'Start the next campaign' },
  length: { id: 'app.sml.promote.reelsPlan.length', defaultMessage: 'Length' },
  lengthDays: {
    id: 'app.sml.promote.reelsPlan.length.days',
    defaultMessage: '{days, plural, one {# day} other {# days}}',
  },
  lengthOther: { id: 'app.sml.promote.reelsPlan.length.other', defaultMessage: 'Other' },
  lengthOtherLabel: { id: 'app.sml.promote.reelsPlan.length.otherLabel', defaultMessage: 'Days of the campaign' },
  lengthRange: { id: 'app.sml.promote.reelsPlan.length.range', defaultMessage: 'Choose {min} to {max} days.' },
  cadence: { id: 'app.sml.promote.reelsPlan.cadence', defaultMessage: 'How often' },
  cadenceDaily: { id: 'app.sml.promote.reelsPlan.cadence.daily', defaultMessage: 'Every day' },
  cadenceEvery2Days: { id: 'app.sml.promote.reelsPlan.cadence.every2Days', defaultMessage: 'Every 2 days' },
  cadenceThreeAWeek: { id: 'app.sml.promote.reelsPlan.cadence.threeAWeek', defaultMessage: '3 a week' },
  postsPerDay: { id: 'app.sml.promote.reelsPlan.postsPerDay', defaultMessage: 'Posts a day' },
  postsPerDayOption: {
    id: 'app.sml.promote.reelsPlan.postsPerDay.option',
    defaultMessage: '{count} a day',
  },
  postsPerDayHint: {
    id: 'app.sml.promote.reelsPlan.postsPerDay.hint',
    defaultMessage: 'Each post is its own AI reel.',
  },
  postsPerDayLocked: {
    id: 'app.sml.promote.reelsPlan.postsPerDay.locked',
    defaultMessage: 'More than 1 post a day needs a paid plan.',
  },
  starts: { id: 'app.sml.promote.reelsPlan.starts', defaultMessage: 'Starts' },
  channels: { id: 'app.sml.promote.reelsPlan.channels', defaultMessage: 'Channels' },
  changeChannels: { id: 'app.sml.promote.reelsPlan.channels.change', defaultMessage: 'Change channels' },
  channelsDone: { id: 'app.sml.promote.reelsPlan.channels.done', defaultMessage: 'Done' },
  strip: {
    id: 'app.sml.promote.reelsPlan.strip',
    defaultMessage:
      '{reels, plural, one {# day of the campaign gets a reel} other {# days of the campaign get a reel}}, out of {days}.',
  },
  // ---- The foot line (f1, f2)
  // QA finding B14: the line says where the post count comes from (reels × channels).
  summary: {
    id: 'app.sml.promote.reelsPlan.summary.perChannel',
    defaultMessage:
      '<b>{reels, plural, one {# reel} other {# reels}}</b> · <b>{credits, plural, one {# video credit} other {# video credits}}</b> · {posts, plural, one {# post} other {# posts}} ({reels, plural, one {# reel} other {# reels}} × {channels, plural, one {# channel} other {# channels}} that take a video), from {from} to {to}',
  },
  summaryReels: {
    id: 'app.sml.promote.reelsPlan.summary.reels',
    defaultMessage: '<b>{reels, plural, one {# reel} other {# reels}}</b>',
  },
  have: {
    id: 'app.sml.promote.reelsPlan.have',
    defaultMessage: 'You have {left, plural, one {# credit} other {# credits}}',
  },
  make: { id: 'app.sml.promote.reelsPlan.make', defaultMessage: 'Start the campaign' },
  noteShort: {
    id: 'app.sml.promote.reelsPlan.note.short',
    defaultMessage:
      'This campaign needs {cost, plural, one {# credit} other {# credits}} and you have {left}. Nothing starts until the credits cover every reel.',
  },
  noteBuyMore: {
    id: 'app.sml.promote.reelsPlan.note.buyMore',
    defaultMessage: 'Buy {missing, plural, one {# more credit} other {# more credits}} to start this campaign.',
  },
  scheduledLeft: {
    id: 'app.sml.promote.reelsPlan.scheduledLeft',
    defaultMessage: '{posts, plural, one {# post} other {# posts}} · {left} left this month in your plan',
  },
  noteRepeat: {
    id: 'app.sml.promote.reelsPlan.note.repeat',
    defaultMessage:
      'A long campaign can repeat parts of the song. This song has {parts, plural, one {# strong part} other {# strong parts}}; the clips and the texts still differ.',
  },
  // The server answers `needsPlan` for an older song on Free and for a workspace with no right to
  // AI reels: the sentence is true for both and names no price.
  refusalNeedsPlan: {
    id: 'app.sml.promote.reelsPlan.refusal.needsPlan',
    defaultMessage: 'An auto campaign for this song needs a higher plan.',
  },
  seePlans: { id: 'app.sml.promote.reelsPlan.seePlans', defaultMessage: 'See plans' },
  refusalPostsPerDayNeedsPlan: {
    id: 'app.sml.promote.reelsPlan.refusal.postsPerDayNeedsPlan',
    defaultMessage: 'More than 1 post a day needs a paid plan.',
  },
  refusalNoMaster: {
    id: 'app.sml.promote.reelsPlan.refusal.noMaster',
    defaultMessage: 'Add the song file first. An AI reel is cut from the master.',
  },
  openSong: { id: 'app.sml.promote.reelsPlan.openSong', defaultMessage: 'Open the song' },
  refusalMasterProcessing: {
    id: 'app.sml.promote.reelsPlan.refusal.masterProcessing',
    defaultMessage: 'We read the song file. Try again in a few minutes.',
  },
  refusalUnavailable: {
    id: 'app.sml.promote.reelsPlan.refusal.unavailable',
    defaultMessage: 'AI reels are not available now. Try again later.',
  },
  refusalNoChannels: {
    id: 'app.sml.promote.reelsPlan.refusal.noChannels',
    defaultMessage: 'Connect a channel that takes reels.',
  },
  openChannels: { id: 'app.sml.promote.reelsPlan.openConnections', defaultMessage: 'Open Connections' },
  refusalPlanExists: {
    id: 'app.sml.promote.reelsPlan.refusal.planExists',
    defaultMessage: 'This song has an auto campaign already.',
  },
  previewFailed: {
    id: 'app.sml.promote.reelsPlan.previewFailed',
    defaultMessage: 'We could not check this campaign. Change a setting or try again.',
  },
  // ---- Run sheet (f3, f6)
  settingsLine: {
    id: 'app.sml.promote.reelsPlan.settingsLine',
    defaultMessage:
      '{days, plural, one {# day} other {# days}} · {cadence, select, daily {every day} every2Days {every 2 days} other {3 a week}} · from {from}',
  },
  settingsLinePosts: {
    id: 'app.sml.promote.reelsPlan.settingsLinePosts',
    defaultMessage:
      '{days, plural, one {# day} other {# days}} · {cadence, select, daily {every day} every2Days {every 2 days} other {3 a week}} · {posts} posts a day · from {from}',
  },
  readyCount: {
    id: 'app.sml.promote.reelsPlan.readyCount',
    defaultMessage: '{ready} of {count, plural, one {# reel} other {# reels}} ready',
  },
  channelsLine: {
    id: 'app.sml.promote.reelsPlan.channelsLine',
    defaultMessage: 'Drafts appear when a reel is ready.',
  },
  endPlan: { id: 'app.sml.promote.reelsPlan.end', defaultMessage: 'End the campaign' },
  endShort: { id: 'app.sml.promote.reelsPlan.end.short', defaultMessage: 'End' },
  stepTitle: { id: 'app.sml.promote.reelsPlan.step.title', defaultMessage: 'AI reel {number}' },
  stepDay: { id: 'app.sml.promote.reelsPlan.step.day', defaultMessage: 'Day {number}' },
  stepDayTitle: { id: 'app.sml.promote.reelsPlan.step.dayTitle', defaultMessage: 'AI reel, day {number}' },
  stepDayPost: {
    id: 'app.sml.promote.reelsPlan.step.dayPost',
    defaultMessage: 'Day {day} · Post {number} of {count}',
  },
  stepPostOf: { id: 'app.sml.promote.reelsPlan.step.postOf', defaultMessage: 'Post {number} of {count}' },
  stepDayPostTitle: {
    id: 'app.sml.promote.reelsPlan.step.dayPostTitle',
    defaultMessage: 'AI reel, day {day}, post {number}',
  },
  stepQueued: { id: 'app.sml.promote.reelsPlan.step.queued', defaultMessage: 'Queued' },
  stepMaking: { id: 'app.sml.promote.reelsPlan.step.making', defaultMessage: 'Making the reel · {percent}%' },
  stepFailed: {
    id: 'app.sml.promote.reelsPlan.step.failed',
    defaultMessage: 'The reel did not finish. 1 credit came back.',
  },
  makeAgain: { id: 'app.sml.promote.reelsPlan.step.makeAgain', defaultMessage: 'Make it again' },
  moreDays: {
    id: 'app.sml.promote.reelsPlan.moreDays',
    defaultMessage: '{count, plural, one {# more day} other {# more days}}: {days}',
  },
  morePosts: {
    id: 'app.sml.promote.reelsPlan.morePosts',
    defaultMessage: '{count, plural, one {# more post} other {# more posts}}: {days}',
  },
  // ---- "End the campaign" and the closed line (f4)
  endTitle: { id: 'app.sml.promote.reelsPlan.end.title', defaultMessage: 'End the auto campaign?' },
  endBody: {
    id: 'app.sml.promote.reelsPlan.end.body',
    defaultMessage:
      'This deletes {drafts, plural, one {# draft} other {# drafts}} and cancels {scheduled, plural, one {# scheduled post} other {# scheduled posts}}. Posts that went out stay. The {reels, plural, one {# reel stays} other {# reels stay}} in Posts, and their credits do not come back.',
  },
  endBodyEmpty: {
    id: 'app.sml.promote.reelsPlan.end.bodyEmpty',
    defaultMessage: 'This ends the campaign. The reels stay in Posts.',
  },
  endKeep: { id: 'app.sml.promote.reelsPlan.end.keep', defaultMessage: 'Keep the campaign' },
  closedLine: {
    id: 'app.sml.promote.reelsPlan.closed',
    defaultMessage: '<b>Auto campaign</b> · {reels, plural, one {# reel} other {# reels}} · {from} to {to}',
  },
  seePosts: { id: 'app.sml.promote.reelsPlan.seePosts', defaultMessage: 'See the posts' },
  // ---- The limit upsell of the setup (design limit-upsell a1, a6)
  fitDays: {
    id: 'app.sml.promote.reelsPlan.upsell.fitDays',
    defaultMessage: 'Plan only what fits ({days} days)',
  },
  buyPack: { id: 'app.sml.promote.reelsPlan.upsell.buyPack', defaultMessage: 'Buy a credit pack' },
});
